/**
 * 도메인 핵심 타입 정의.
 * Firestore 문서 구조와 1:1 대응시키되, 선택 필드는 Firestore의 부재(null)도 허용한다.
 */

// ── 공통 ──────────────────────────────────────────────────────────────

/** 지원 로케일 (요구사항 7) */
export const SUPPORTED_LOCALES = ['ko', 'en', 'ja', 'zh', 'vi', 'es'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'ko';

/** Firestore Timestamp (불투명 타입 — SDK 의존 제거) */
export interface Timestamp {
  seconds: number;
  nanoseconds: number;
  toDate(): Date;
}

export type IsoDate = string;

// ── 사용자 ────────────────────────────────────────────────────────────

export interface UserSettings {
  /** 테마: 시스템跟随 / 라이트 / 다크 */
  theme: 'system' | 'light' | 'dark';
  /** Enter로 전송 (줄바꿈은 Shift+Enter) */
  enterToSend: boolean;
  /** 읽음 표시 수신 허용 (3단계 틱) */
  readReceipts: boolean;
  /** 타이핑 표시 송신 허용 */
  typingIndicator: boolean;
  /** 모바일 데이터에서 자동 다운로드 (인시더리트 메시지의 반대) */
  autoDownloadOnCellular: boolean;
  /** 더블탭/더블클릭으로 바로 넣는 기본 리액션 (A-4) */
  doubleTapReaction: string;
  /** 상태 메시지 */
  statusText?: string;
  /** 번역 설정 (5-7-1장) */
  translation: TranslationGlobalSettings;
  /** 2단계 PIN 잠금 (PLAN 13-3장) */
  pin: PinSetting;
}

export interface PinSetting {
  /** PBKDF2-SHA256 해시 (100k iter, salt 포함) */
  hash: string;
  /** 솔트 (base64) */
  salt: string;
  /** 마지막 갱신 시각 */
  updatedAt: number;
}

export interface User {
  /** 소문자 정규화된 유니크 ID (handles/{id} 문서 ID와 동일) */
  handle: string;
  /** 표기용 원본 케이스 */
  handleDisplay: string;
  displayName: string;
  photoURL?: string;
  locale: Locale;
  settings: UserSettings;
  createdAt: Timestamp;
  lastSeenAt: Timestamp;
  deletedAt?: Timestamp;
}

// ── 대화방 ────────────────────────────────────────────────────────────

export type ConvoType = 'dm' | 'group';
export type MemberRole = 'owner' | 'admin' | 'member';
export type AdmissionStatus = 'pending' | 'approved' | 'rejected';

export interface Convo {
  type: ConvoType;
  /** group일 때만 존재. dm은 상대 닉네임을 실시간 계산 */
  title?: string;
  photoURL?: string;
  createdBy: string;
  /** 정렬 고정 — Rules의 arrayContains 검증과 결정론적 순서 보장 */
  memberIds: string[];
  adminIds: string[];
  /** DM 1:1 보장용 정렬 키 'uidA_uidB' */
  dmKey?: string;
  /** 방 단위 설정 (사라지는 메시지 TTL 등) */
  settings: ConvoSettings;
  lastMessage?: LastMessagePreview;
  /** 관리자가 설정한 참여자 별칭 (참여자 uid -> alias) */
  memberAliases?: Record<string, string>;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  /** 관리자에 의한 방 폭파/예약 폭파 정보 */
  destructionNotice?: ConvoDestructionNotice;
  destroyedAt?: Timestamp;
}

export interface ConvoDestructionNotice {
  reason: string;
  destroyedBy: string;
  destroyedByName?: string;
  createdAt: number;
  scheduledAt?: number;
  status: 'scheduled' | 'executed';
}

export interface ConvoSettings {
  /** 0 = 꺼짐, 86400 = 24시간, 604800 = 7일, 7776000 = 90일 */
  disappearingTtlSeconds: 0 | 86400 | 604800 | 7776000;
  /** 인시더리트 방 (모바일 데이터 자동다운로드 차단) */
  private: boolean;
}

export interface LastMessageReaction {
  emoji: string;
  reactorUid: string;
  messageId: string;
  targetSenderId?: string;
  at: number;
}

export interface LastMessagePreview {
  text: string;
  senderId: string;
  type: MessageType;
  createdAt: Timestamp;
  /** 마지막 메시지 문서 ID — 인앱 배너가 그 메시지로 바로 이동하는 데 쓴다 (PLAN 5-8-4장).
      이 필드가 생기기 전에 쓰인 문서에는 없을 수 있다. */
  messageId?: string;
  /** 마지막 메시지의 리액션 요약 (이모지 → 인원수).
      목록에서 리액션 줄을 그리려고 메시지 문서를 다시 읽지 않도록 비정규화한다 (PLAN 6-8장). */
  reactions?: Record<string, number>;
  /** 가장 최근에 추가된 리액션 행위자 메타데이터 — 인앱 배너가 자기 리액션(Self-Reaction)을 제외하고
      내 메시지에 상대방이 남긴 리액션만 정확히 표시하기 위해 사용한다 (PLAN enable_noti v1.1.0). */
  lastReaction?: LastMessageReaction;
}

export interface ConvoMember {
  role: MemberRole;
  /** 2단계 참여 승인 상태 ('pending' | 'approved' | 'rejected') */
  admissionStatus?: AdmissionStatus;
  /** 첫 진입 시 관리자에게 보낸 자기소개 텍스트 */
  introMessage?: string;
  /** 첫 진입 시 관리자에게 보낸 신원 확인 사진 URL */
  introPhotoUrl?: string;
  /** 관리자가 부여한 별칭 */
  alias?: string;
  /** 사용자 본인이 설정한 대화방 내 대화명 (닉네임) */
  nickname?: string;
  /** 가입 시 사용한 Google 이메일 */
  userEmail?: string;
  /** 티켓 토큰 (진입 검증용) */
  joinedViaTicket?: string;
  /** 승인 요청 시각 */
  requestedAt?: Timestamp;
  /** 관리자 승인 시각 */
  approvedAt?: Timestamp;
  /** 승인한 관리자 UID */
  approvedBy?: string;
  joinedAt: Timestamp;
  lastReadAt: Timestamp;
  unreadCount: number;
  mutedUntil?: Timestamp;
  pinned: boolean;
  archived: boolean;
  leftAt?: Timestamp;
  /** 강제 퇴실 시 퇴실시킨 관리자 ID (자발적 퇴실과 구분) */
  removedBy?: string;
  /** 번역 토글 (5-7-1장) */
  translation: RoomTranslationSettings;
  /** 방별 채팅 테마 — 자기 설정만 바꾼다. 방을 건드리지 않았으면 앱 설정을 따른다 */
  theme?: RoomTheme;
}

/**
 * 방별 채팅 테마 (`members/{uid}.theme`).
 *
 * 전역 테마(`UserSettings.theme`)를 대체하지 않고 겹쳐 쓴다. `dark`를 비워 두면
 * 그 방에서만큼은 앱 설정을 그대로 따른다 — 별개 주제를 정한 방만 달라진다.
 */
export interface RoomTheme {
  /** undefined = 자동(앱 설정 따름) · true = 다크 · false = 라이트 */
  dark?: boolean;
  /** 말풍선 강조색 (CSS 색상 문자열) — 없으면 토큰 기본값 */
  accent?: string;
  /** 배경 이미지 다운로드 URL — 없으면 평평한 배경 */
  backgroundUrl?: string;
}

// ── 메시지 ────────────────────────────────────────────────────────────

export type MessageType = 'text' | 'image' | 'video' | 'audio' | 'file' | 'system';

export interface MediaMeta {
  path: string;
  mime: string;
  size: number;
  width?: number;
  height?: number;
  durationMs?: number;
  thumbnailPath?: string;
  /** 원본 표시명 (file 타입) */
  fileName?: string;
  /** 업로드 완료 시 함께 기록하는 다운로드 URL.
      문서에 넣어 두면 버블 렌더 때 Storage 읽기가 0회가 된다. */
  url?: string;
  /** 썸네일 다운로드 URL (목록/대화방에서 원본 없이 그리기 위함) */
  thumbnailUrl?: string;
}

export interface Message {
  id: string;
  convoId: string;
  type: MessageType;
  senderId: string;
  text?: string;
  media?: MediaMeta;
  replyTo?: string;
  forwardedFrom?: string;
  /** CJK는 2-gram, 라틴계는 단어 (5-4-1장 검색) */
  searchTokens?: string[];
  reactions: Record<string, string[]>;
  reactionCount: number;
  editedAt?: Timestamp;
  deletedAt?: Timestamp;
  createdAt: Timestamp;
  clientCreatedAt: number;
  sourceDevice?: 'web' | 'share';
  /** 사라지는 메시지 만료 시각 (서버측 배치 삭제용) */
  expiresAt?: Timestamp;
}

export interface MessageTranslation {
  text: string;
  sourceLang: Locale;
  targetLang: Locale;
  model: string;
  createdAt: Timestamp;
}

// ── 번역 ──────────────────────────────────────────────────────────────

export type TranslationMode = 'always' | 'incoming' | 'notification' | 'off';
export type RoomTranslationMode = 'inherit' | TranslationMode;

export interface TranslationGlobalSettings {
  globalMode: TranslationMode;
  targetLang: Locale;
  autoDetectTarget: boolean;
  defaultPerRoomMode: RoomTranslationMode;
  /** 일일 번역 문자 한도 (PLAN 5-7-5장) */
  dailyCharLimit: number;
}

export interface RoomTranslationSettings {
  mode: RoomTranslationMode;
  targetLangOverride?: Locale;
  updatedAt: Timestamp;
}

// ── 친구 / 차단 ───────────────────────────────────────────────────────

export type FriendRequestVia = 'id' | 'qr' | 'group' | 'qrcode-scan';
export type FriendRequestStatus = 'pending' | 'accepted' | 'declined' | 'cancelled' | 'expired';

export interface FriendRequest {
  id: string;
  requesterId: string;
  recipientId: string;
  /** 요청 시점 표기 스냅샷 — 이후 ID가 바뀌어도 이름 유지 */
  displayNameAtRequest: string;
  handleAtRequest: string;
  via: FriendRequestVia;
  message?: string;
  status: FriendRequestStatus;
  createdAt: Timestamp;
  respondedAt?: Timestamp;
}

export interface Block {
  blockerId: string;
  blockedId: string;
  createdAt: Timestamp;
  reason?: string;
}

// ── 알림 / 푸시 ───────────────────────────────────────────────────────

export type PushPlatform = 'web' | 'ios' | 'android';

export interface PushToken {
  platform: PushPlatform;
  ua?: string;
  deviceName?: string;
  createdAt: Timestamp;
  lastSeenAt: Timestamp;
}

export interface PrivacySettings {
  notificationsOff: boolean;
  readReceipts: boolean;
  typingIndicator: boolean;
  lastSeen: 'all' | 'contacts' | 'nobody';
  inAppBanners: boolean;
  inAppBannerDurationMs: number;
  /** 잠금 화면 알림 미리보기 (기본 true) — 5-10장 */
  previewOnLock: boolean;
  autoDownloadOnCellular: boolean;
}

// ── 어드민 ────────────────────────────────────────────────────────────

export type ReportStatus = 'open' | 'reviewing' | 'resolved' | 'dismissed';
export type ReportTargetType = 'message' | 'user';

export interface Report {
  id: string;
  reporterId: string;
  targetType: ReportTargetType;
  targetId: string;
  reason: string;
  status: ReportStatus;
  createdAt: Timestamp;
  resolvedAt?: Timestamp;
}

export interface AuditLog {
  id: string;
  actorId: string;
  action: string;
  target: string;
  meta: Record<string, unknown>;
  createdAt: Timestamp;
}

// ── 초대 요청 (일반 사용자 → Admin, 1.0.1) ───────────────────────────

export interface InviteRequestDoc {
  id: string;
  uid: string;
  userEmail: string;
  userName: string;
  userHandle?: string;
  message: string;
  photoUrl?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: number;
  handledAt?: number;
  convoId?: string;
}
